import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { MdError } from 'react-icons/md';
import { RiArrowLeftSLine } from 'react-icons/ri';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Grid, Tooltip } from '@material-ui/core';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { AxiosError } from 'axios';
import { FaCircleQuestion } from 'react-icons/fa6';
import { TiDownload } from 'react-icons/ti';
import { Advice } from '../../../../../components/Advice';
import Button from '../../../../../components/Button';
import Input from '../../../../../components/Input';
import PageTitle from '../../../../../components/PageTitle';
import Select from '../../../../../components/Select';
import Slider from '../../../../../components/Slider';
import { useToast } from '../../../../../hooks/toast';
import { IDevice, ISensor } from '../../../../../interfaces';
import api from '../../../../../services/api';
import getValidationErrors from '../../../../../utils/getValidationsErrors';
import DownloadCSV from '../../../components/DownloadCSV';
import * as S from './styles';

type UpdateSensorFormData = {
  id?: string;
  sensor?: string;
  port?: number;
  reading?: string | null;
  channel_type?: number;
  unit_of_measurement?: string;
  is_enabled?: boolean;
  is_cumulative?: boolean;
  enable_commands?: boolean;
};

type UpdateEquationFormData = {
  id: string;
  description: string;
  equation: string;
  is_enabled: boolean;
  input: number[];
  output: number[];
};

type UpdateLimitFormData = {
  limit_id: string;
  max: number;
  min: number;
  is_enabled: boolean;
  email_alert: boolean;
  waiting_time: number;
};

const channelTypes = [
  { id: '3', name: 'VBat' },
  { id: '4', name: 'Temp' },
  { id: '6', name: 'GSM Signal' },
  { id: '7', name: 'GPS' },
  { id: '9', name: 'Digital' },
  { id: '11', name: 'Serial SINT32' },
  { id: '13', name: 'Analog S32' },
  { id: '15', name: 'LORA Signal' },
  { id: '101', name: 'Battery Consumption' },
  { id: '102', name: 'Communication Status' }
];

const analogPortOptions = [
  { id: 'porta_analogica_1', name: 'Porta Analógica 1' },
  { id: 'porta_analogica_2', name: 'Porta Analógica 2' },
  { id: 'contador_pulsos_total', name: 'Contador de pulsos total' },
  { id: 'contador_pulsos_parcial', name: 'Contador de pulsos parcial' },
  { id: 'pulsos_por_segundo', name: 'Pulsos por segundo' },
];

const EditSensor: React.FC = () => {
  const [editEquation, setEditEquation] = useState(false);
  const [editAlert, setEditAlert] = useState(false);
  const [sensor, setSensor] = useState<ISensor>({} as ISensor);
  const [error, setError] = useState('');

  const [showDownloadData, setShowDownloadData] = useState(false);
  const [showDownloadCommands, setShowDownloadCommands] = useState(false);
  const [device, setDevice] = useState<IDevice | null>(null);

  const { addToast } = useToast();
  const params = useParams() as { id: string };
  const history = useHistory();
  const equationFormRef = useRef<FormHandles>(null);
  const alertFormRef = useRef<FormHandles>(null);
  const sensorFormRef = useRef<FormHandles>(null);

  const searchSensor = useCallback(async () => {
    try {
      const { data } = await api.get<ISensor>('/sensors', { params: { sensor_id: params.id } });

      if (!data?.id) {
        setError('Dispositivo não encontrado');

        return;
      }

      setSensor(data);
    } catch (error) {
      const message = (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`;

      addToast({
        type: 'error',
        title: 'Erro ao buscar sensor',
        description: message,
      });

      setError(message);
    }
  }, [addToast]);

  useEffect(() => {
    searchSensor();
  }, [searchSensor]);

  const updateSensorFormHandler = useCallback(
    async (formData: UpdateSensorFormData) => {
      try {
        sensorFormRef.current?.setErrors({});

        const isVersion4Device = device && String(device.version) === '4';

        const schema = Yup.object().shape({
          sensor: Yup.string().required(),
          unit_of_measurement: Yup.string().required(),
           port: isVersion4Device
            ? Yup.mixed().notRequired()
            : Yup.number().typeError('Porta inválida').min(1).max(240).required(),
          reading: isVersion4Device ? Yup.string().required('Selecione a entrada') : Yup.string().notRequired(),
          channel_type: device && String(device.version) === '3'
            ? Yup.string().required('Channel type é obrigatório')
            : Yup.string().notRequired(),
          is_enabled: Yup.string().required(),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

         const {
          sensor: sensorName,
          unit_of_measurement,
          port,
          reading,
          channel_type,
          is_enabled,
          is_cumulative,
          enable_commands,
        } = formData;

        const numericPort = Number(port);

        const dataToSend = {
          id: params.id,
          name: sensorName,
          port: isVersion4Device ? 1 : Number.isNaN(numericPort) ? sensor?.port ?? 0 : numericPort,
          reading: isVersion4Device ? (reading || null) : null,
          channel_type: channel_type ? Number(channel_type) : undefined,
          is_enabled,
          unit_of_measurement,
          is_cumulative,
          enable_commands
        };

        await api.put('/sensors', dataToSend);

        addToast({
          type: 'success',
          title: 'Sensor atualizado!',
          description: 'Sensor atualizado com sucesso!',
        });

        return searchSensor();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return sensorFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao atualizar sensor',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, params.id, searchSensor, device, sensor.port],
  );

  const fetchDeviceBySensor = useCallback(async (sensorDeviceId: string) => {
    try {
      const { data } = await api.get<IDevice>(`/devices/${sensorDeviceId}`);
      setDevice(data);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        return sensorFormRef.current?.setErrors(errors);
      }
      return addToast({
        type: 'error',
        title: 'Erro ao buscar dispositivo',
        description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
      });
    }
  }, []);

  useEffect(() => {
    const fetchSensorAndDevice = async () => {
      try {
        const { data: sensorData } = await api.get<ISensor>('/sensors', { params: { sensor_id: params.id } });
        setSensor(sensorData);
        if (sensorData.device_id) {
          await fetchDeviceBySensor(sensorData.device_id);
        }
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return sensorFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao buscar sensor',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    };
    fetchSensorAndDevice();
  }, [params.id, fetchDeviceBySensor]);

  const updateEquationFormHandler = useCallback(
    async (formData: UpdateEquationFormData) => {
      try {
        equationFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          description: Yup.string().required(),
          equation: Yup.string().required(),
          is_enabled: Yup.boolean().required(),
          input: Yup.array(Yup.number()).required(),
          output: Yup.array(Yup.number()).required(),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        await api.put('/equation', { ...formData, id: sensor.equation.id });

        sensorFormRef.current?.reset();

        addToast({
          type: 'success',
          title: 'Equação atualizada!',
          description: 'Equação atualizada com sucesso!',
        });

        setEditEquation(false);
        searchSensor();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          return equationFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao atualizar equação',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, params.id, searchSensor, sensor],
  );

  const limitFormHandler = useCallback(
    async (formData: UpdateLimitFormData) => {
      try {
        alertFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          max: Yup.string().required(),
          min: Yup.string().required(),
          waiting_time: Yup.string().required(),
          email_alert: Yup.boolean().required(),
          is_enabled: Yup.boolean().required(),
        });

        await schema.validate(formData, {
          abortEarly: false,
        });

        await api.put('/limits', { ...formData, limit_id: sensor.limit.id });

        sensorFormRef.current?.reset();

        addToast({
          type: 'success',
          title: 'Alerta atualizado!',
          description: 'Alerta atualizado com sucesso!',
        });

        setEditAlert(false);
        searchSensor();

        // Sync Device
        await syncDeviceFormHandler();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          return alertFormRef.current?.setErrors(errors);
        }
        return addToast({
          type: 'error',
          title: 'Erro ao atualizar alerta',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, params.id, searchSensor, sensor],
  );

  const syncDeviceFormHandler = useCallback(
    async () => {
      try {
        if (!sensor.device_id || sensor.port === undefined || sensor.port === null) {
          addToast({
            type: 'error',
            title: 'Erro ao sincronizar',
            description: !sensor.device_id
              ? 'ID do dispositivo não encontrado'
              : 'Porta do sensor não encontrada',
          });
          return;
        }

        const formData = alertFormRef.current?.getData() as any;

        const commandParams = {
          max: Number(formData?.max ?? sensor.limit?.max),
          min: Number(formData?.min ?? sensor.limit?.min),
          enabled: formData?.is_enabled !== undefined ? formData.is_enabled : sensor.limit?.is_enabled,
          waitTime: Number(formData?.waiting_time ?? sensor.limit?.waiting_time),
        };

        await api.post(`/devices/${sensor.device_id}/${sensor.id}/send-command`, {
          port: Number(sensor.port),
          commandName: 'synchronize_device',
          params: commandParams,
          retain: false,
        });

        addToast({
          type: 'success',
          title: 'Sincronização realizada!',
          description: 'Comando enviado para o dispositivo com sucesso!',
        });
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao sincronizar',
          description: (error as AxiosError).response ? `${(error as AxiosError).response?.data.message}` : `${error}`,
        });
      }
    },
    [addToast, sensor],
  );

  const infoSensor = useMemo(() => {
    const isVersion4Device = device && String(device.version) === '4';

    return (
      <Form className="form" ref={sensorFormRef} onSubmit={updateSensorFormHandler}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <S.ButtonExportCSV type="button" onClick={() => setShowDownloadData(true)}>
            <TiDownload size="1.5rem" />
            Exportar Alertas
          </S.ButtonExportCSV>
          
          <S.ButtonExportCSV type="button" onClick={() => setShowDownloadCommands(true)}>
            <TiDownload size="1.5rem" />
            Exportar Comandos
          </S.ButtonExportCSV>
        </div>

        <Grid container spacing={2} className="inputs">
          <Grid item xs={12}>
            <p>Sensor</p>
            <Input hideErrorIcon name="sensor" type="text" defaultValue={sensor.name} />
          </Grid>
          <div className="s2">
            <Grid item xs={5}>
              <p>Unidade de medida</p>
              <Input hideErrorIcon name="unit_of_measurement" type="text" defaultValue={sensor.unit_of_measurement} />
            </Grid>
            {isVersion4Device && (
              <Grid item xs={5}>
                <p>Leitura</p>
                <Select
                  name="reading"
                  defaultValue={sensor.reading ?? ''}
                  data={analogPortOptions}
                  showEmptyOption={false}
                />
              </Grid>
            )}
            {!isVersion4Device && typeof sensor.port !== 'undefined' && (
              <Grid item xs={5}>
                <p>Porta</p>
                <Input hideErrorIcon name="port" type="number" defaultValue={sensor.port ?? ''} />
              </Grid>
            )}
          </div>

          {device && device.version && String(device.version) === '3' && (
            <div className="s2">
              <Grid item xs={12}>
                <p>Channel Type</p>
                <Select
                  name="channel_type"
                  defaultValue={sensor.channel_type?.toString() ?? ''}
                  data={channelTypes}
                />
              </Grid>
            </div>
          )}

        </Grid>

        <div className="butts">
          <Grid item xs={3}>
            <p>Status</p>
            <Slider name="is_enabled" checked={sensor.is_enabled} />
          </Grid>
          <Grid item xs={3}>
            <p>Cumulativo</p>
            <Slider name="is_cumulative" checked={sensor.is_cumulative} />
          </Grid>
           <Grid item xs={3}>
            <p>Comandos</p>
            <Slider name="enable_commands" checked={sensor.enable_commands} />
          </Grid>

          <div className="save-button-wrapper">
            <Button type="submit">Salvar Alterações</Button>
          </div>
        </div>
      </Form>
    );
  }, [updateSensorFormHandler, sensor, device]);

  const alertEquationSensor = useMemo(() => {
    return (
      <div className="cards">
        <Grid item xs={12}>
          <h2>Equação</h2>
          <button type="button" className="equation" onClick={() => setEditEquation(true)}>
            <span className={sensor.equation?.is_enabled ? '' : 'disable'} />

            <div className="start">
              <h3>Filtro de Entrada</h3>
              <h3>De</h3>
              <p>{sensor.equation?.input[0]}</p>
              <h3>Até</h3>
              <p>{sensor.equation?.input[1]}</p>
            </div>
            <div className="description">
              <h3>Descrição</h3>
              <p>{sensor.equation?.description}</p>
              <p>{sensor.equation?.equation}</p>
            </div>
            <div className="end">
              <h3>Filtro de Saida</h3>
              <h3>De</h3>
              <p>{sensor.equation?.output[0]}</p>
              <h3>Até</h3>
              <p>{sensor.equation?.output[1]}</p>
            </div>
          </button>
        </Grid>

        <Grid item xs={12}>
          <h2>Alarme</h2>
          <button type="button" className="alarm" onClick={() => setEditAlert(true)}>
            <span className={sensor.limit?.is_enabled ? '' : 'disable'} />

            <div className="max-min">
              <p>{`max: ${sensor.limit?.max}`}</p>
              <p>{`min: ${sensor.limit?.min}`}</p>
            </div>
            <div className="interval">
              <p>{`Intervalo de tempo: ${sensor.limit?.waiting_time} seg`}</p>
              <p>{`Aviso por e-mail: ${sensor.limit?.email_alert ? 'Ativado' : 'Desativado'}`}</p>
            </div>
          </button>
        </Grid>
      </div>
    );
  }, [sensor]);

  const equationForm = useMemo(() => {
    return (
      <Form className="form" ref={equationFormRef} onSubmit={updateEquationFormHandler}>
        <button type="button" className="close" onClick={() => setEditEquation(false)}>
          <AiOutlineClose />
        </button>
        <Grid item className="description">
          <p>Descrição</p>
          <Input hideErrorIcon name="description" placeholder="Breve descrição da equação" defaultValue={sensor.equation?.description} />
        </Grid>

        <Grid container className="filter-equation">
          <Grid className="start">
            <Grid container style={{ gap: 4, justifyContent: 'center' }}>
              <p>Filtro de Entrada</p>
              <Tooltip style={{ zIndex: 9999 }} title="O filtro de entrada limita o valor máximo ou mínimo recebido pelo IoT.">
                <div>
                  <FaCircleQuestion />
                </div>
              </Tooltip>
            </Grid>
            <Grid className="filter-start">
              <p>De</p>
              <Input hideErrorIcon name="input[0]" type="number" defaultValue={sensor.equation?.input[0]} />
              <p>Até</p>
              <Input hideErrorIcon name="input[1]" type="number" defaultValue={sensor.equation?.input[1]} />
            </Grid>
          </Grid>

          <Grid className="equation">
            <Grid container style={{ gap: 4, justifyContent: 'center' }}>
              <p>Equação</p>
              <Tooltip
                style={{ zIndex: 9999 }}
                title='A equação recebe o valor limitado do filtro de entrada, realiza o cálculo e passa para o filtro de saída. Utilize apenas "x" caso não deseje aplicar um cálculo.'
              >
                <div>
                  <FaCircleQuestion />
                </div>
              </Tooltip>
            </Grid>
            <Grid item className="equation">
              <p className="description">{`"x" será substituído com o valor do sensor no tempo de execução.`}</p>
              <Input hideErrorIcon name="equation" placeholder='("x" / 2) + 2' defaultValue={sensor.equation?.equation} />
            </Grid>
          </Grid>

          <Grid className="end">
            <Grid container style={{ gap: 4, justifyContent: 'center' }}>
              <p>Filtro de Saída</p>
              <Tooltip style={{ zIndex: 9999 }} title="O filtro de saída limita o valor máximo ou mínimo final calculado da equação.">
                <div>
                  <FaCircleQuestion />
                </div>
              </Tooltip>
            </Grid>
            <Grid className="filter-end">
              <p>De</p>
              <Input hideErrorIcon name="output[0]" type="number" defaultValue={sensor.equation?.output[0]} />
              <p>Até</p>
              <Input hideErrorIcon name="output[1]" type="number" defaultValue={sensor.equation?.output[1]} />
            </Grid>
          </Grid>
        </Grid>

        <Grid container className="butts">
          <Grid item>
            <p>Status</p>
            <Slider name="is_enabled" checked={sensor.equation?.is_enabled} />
          </Grid>
          <Button type="submit">Salvar alterações </Button>
        </Grid>
      </Form>
    );
  }, [updateEquationFormHandler, sensor]);

  const limitForm = useMemo(() => {
    return (
      <Form className="form" ref={alertFormRef} onSubmit={limitFormHandler}>
        <button type="button" className="close" onClick={() => setEditAlert(false)}>
          <AiOutlineClose />
        </button>
        <Grid container className="container">
          <Grid>
            <p>Max</p>
            <Input hideErrorIcon name="max" defaultValue={sensor.limit?.max} />
          </Grid>
          <Grid>
            <p>Min</p>
            <Input hideErrorIcon name="min" defaultValue={sensor.limit?.min} />
          </Grid>
          <Grid>
            <p>Tempo de espera (seg)</p>
            <Input hideErrorIcon name="waiting_time" defaultValue={sensor.limit?.waiting_time} />
          </Grid>
          <div className="slides">
            <Grid className="slider">
              <p>Status</p>
              <Slider name="is_enabled" checked={sensor.limit?.is_enabled} />
            </Grid>
            <Grid className="slider">
              <p>Alerta por e-mail </p>
              <Slider name="email_alert" checked={sensor.limit?.email_alert} />
            </Grid>
          </div>
        </Grid>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <Button type="submit" className="submit">
            Salvar
          </Button>
          {/* <Button type="button"   className="submit" onClick={syncDeviceFormHandler}>
            Sincronizar
          </Button> */}
        </div>
      </Form>
    );
  }, [limitFormHandler, syncDeviceFormHandler, sensor]);

  return (
    <>
      <S.GoBack>
        <button type="button" onClick={() => history.goBack()}>
          <RiArrowLeftSLine />
          <p>Voltar</p>
        </button>
      </S.GoBack>

      {!!error ? (
        <Advice text={error} icon={MdError} />
      ) : (
        <>
          {!(editEquation || editAlert) && sensor.id && (
            <>
              <PageTitle sub="Sensor" title="Configurar" />
              <S.Container>
                <S.InfoSensor>{infoSensor}</S.InfoSensor>
                <div className="separator" />
                <S.AlertEquationSensors>{alertEquationSensor}</S.AlertEquationSensors>
              </S.Container>
            </>
          )}
          {editEquation && <S.EditEquationPopup>{equationForm}</S.EditEquationPopup>}
          {editAlert && <S.EditAlertPopup>{limitForm}</S.EditAlertPopup>}
        </>
      )}

      <DownloadCSV show={showDownloadData} closeFunction={setShowDownloadData} sensorId={params.id} csvUrl="alerts/csv" />
      <DownloadCSV show={showDownloadCommands} closeFunction={setShowDownloadCommands} sensorId={params.id} csvUrl="devices/commands/export-csv" />
    </>
  );
};

export default EditSensor;
